import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type CompanyGroupingMode = 'grouped' | 'ungrouped';

const STORAGE_KEY = 'companyGroupingMode';

interface CompanyGroupingPreferenceContextValue {
  mode: CompanyGroupingMode;
  setMode: (mode: CompanyGroupingMode) => void;
  toggle: () => void;
}

/**
 * Whether the SRP groups a company's multiple open roles into one HitCompanyGroupCard (see
 * utils/srp/group-hits-by-company.ts) or lists every job as its own card. Defaults to ungrouped
 * (temporarily disabled by default) — client-only (localStorage), not read during SSR, so the
 * static/first-hydrated paint always shows ungrouped, same as every visitor gets today; a
 * returning visitor's "grouped" choice applies after mount.
 */
const CompanyGroupingPreferenceContext = createContext<CompanyGroupingPreferenceContextValue>({
  mode: 'ungrouped',
  setMode: () => {},
  toggle: () => {},
});

export function CompanyGroupingPreferenceProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setModeState] = useState<CompanyGroupingMode>('ungrouped');

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'grouped' || stored === 'ungrouped') {
        setModeState(stored);
      }
    } catch {
      // localStorage unavailable (private browsing, etc.) — stay on the default.
    }
  }, []);

  const setMode = useCallback((next: CompanyGroupingMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence only.
    }
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === 'grouped' ? 'ungrouped' : 'grouped');
  }, [mode, setMode]);

  return (
    <CompanyGroupingPreferenceContext.Provider value={{ mode, setMode, toggle }}>
      {children}
    </CompanyGroupingPreferenceContext.Provider>
  );
}

export function useCompanyGroupingPreference(): CompanyGroupingPreferenceContextValue {
  return useContext(CompanyGroupingPreferenceContext);
}
